import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { TypedDateInput } from "@/components/TypedDateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const createDraft = (job, project) => ({
  scheduled_date: job?.scheduled_date || project.scheduled_date || "",
  foreman: job?.foreman || "",
  crew_members: (job?.crew_members || []).join(", "),
  assignment_notes: job?.assignment_notes || "",
  status: job?.status || "scheduled",
});

export const ProjectActiveJobDrawer = ({
  canHumanAuthority,
  job,
  onClose,
  onSaved,
  project,
}) => {
  const [draft, setDraft] = useState(() => createDraft(job, project));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(createDraft(job, project));
    setError("");
  }, [job, project]);

  const update = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      scheduled_date: draft.scheduled_date,
      foreman: draft.foreman.trim(),
      crew_members: draft.crew_members
        .split(",")
        .map((member) => member.trim())
        .filter(Boolean),
      assignment_notes: draft.assignment_notes.trim(),
      status: draft.status,
    };

    try {
      if (job?.id) {
        await axios.patch(`${API}/active-jobs/${job.id}`, payload);
      } else {
        await axios.post(`${API}/active-jobs`, {
          ...payload,
          project_id: project.id,
          active: true,
        });
      }
      toast.success(job?.id ? "Field assignment saved." : "Field assignment added.");
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail || "Field assignment could not be saved.",
      );
    } finally {
      setSaving(false);
    }
  };

  const changeActivity = async (action) => {
    if (!job?.id || !window.confirm(`Confirm: ${action} this field assignment?`)) return;
    try {
      await axios.post(`${API}/active-jobs/${job.id}/${action}`);
      toast.success(`Field assignment ${action}d.`);
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Field assignment could not be updated.");
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="project-record-drawer" data-testid="project-active-job-drawer">
        <SheetHeader>
          <SheetTitle>{job?.id ? "Edit field assignment" : "New field assignment"}</SheetTitle>
          <SheetDescription data-testid="project-active-job-fixed-project">
            {project.project_code} · {project.name}
          </SheetDescription>
        </SheetHeader>
        <form className="project-record-drawer-form" onSubmit={save}>
          {error && (
            <p
              className="project-record-drawer-error"
              data-testid="project-active-job-error"
            >
              {error}
            </p>
          )}
          <label htmlFor="project-active-job-scheduled-date">
            Scheduled date
            <TypedDateInput
              data-testid="project-active-job-scheduled-date"
              id="project-active-job-scheduled-date"
              onChange={(value) => update("scheduled_date", value)}
              value={draft.scheduled_date}
            />
          </label>
          <label htmlFor="project-active-job-status">
            Assignment status
            <select
              data-testid="project-active-job-status"
              id="project-active-job-status"
              onChange={(event) => update("status", event.target.value)}
              value={draft.status}
            >
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="complete">Complete</option>
              <option value="cancelled">Canceled</option>
            </select>
          </label>
          <label htmlFor="project-active-job-foreman">
            Foreman
            <input
              data-testid="project-active-job-foreman"
              id="project-active-job-foreman"
              onChange={(event) => update("foreman", event.target.value)}
              value={draft.foreman}
            />
          </label>
          <label htmlFor="project-active-job-crew-members">
            Crew members
            <input
              data-testid="project-active-job-crew-members"
              id="project-active-job-crew-members"
              onChange={(event) => update("crew_members", event.target.value)}
              placeholder="Separate names with commas"
              value={draft.crew_members}
            />
          </label>
          <label htmlFor="project-active-job-notes">
            Assignment notes
            <textarea
              data-testid="project-active-job-notes"
              id="project-active-job-notes"
              onChange={(event) => update("assignment_notes", event.target.value)}
              value={draft.assignment_notes}
            />
          </label>
          <SheetFooter>
            {canHumanAuthority && job?.id && (
              <button
                data-testid={`project-active-job-${job.active ? "deactivate" : "restore"}-${job.id}`}
                onClick={() => changeActivity(job.active ? "deactivate" : "restore")}
                type="button"
              >
                {job.active ? "Deactivate" : "Restore"}
              </button>
            )}
            <button
              className="board-refresh-button"
              data-testid="project-active-job-cancel-button"
              disabled={saving}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="invoice-primary-button"
              data-testid="project-active-job-save-button"
              disabled={saving}
              type="submit"
            >
              {saving ? "Saving…" : job?.id ? "Save field assignment" : "Add field assignment"}
            </button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};